import { exportInvoicePdf } from './pdfExport';

/**
 * Download a SAVED invoice's PDF without opening it (Invoice Manager
 * "Download" button, Oct 2026). Builds the exact same payload the
 * invoice detail page's Download PDF does for an unedited invoice, so
 * both give the same file.
 *
 * @param {object} invoice   decoded invoice record (with lineItems)
 * @param {object} ctx       { supplier, plantByCode: Map, parts: [] }
 */
export function downloadSavedInvoicePdf(invoice, { supplier, plantByCode, parts } = {}) {
  if (!invoice) return;
  const sc = String(invoice.SupplierCode || '').toUpperCase();
  // Plant code → distinct part numbers this supplier ships there, to
  // back-fill partNumberLabel on older invoices (same as the detail page).
  const pnSets = new Map();
  for (const p of parts || []) {
    if (String(p.SupplierCode || '').toUpperCase() !== sc) continue;
    const num = String(p.PartNumber || '').trim();
    if (!num) continue;
    for (const code of p.plantCodes || []) {
      const key = String(code || '').toUpperCase();
      if (!key) continue;
      if (!pnSets.has(key)) pnSets.set(key, new Set());
      pnSets.get(key).add(num);
    }
  }
  const lines = (Array.isArray(invoice.lineItems) ? invoice.lineItems : []).map((li) => {
    const plantCode = String(li.plantCode || '').trim().toUpperCase();
    return {
      ...li,
      plantCode,
      plantTitle: plantByCode?.get?.(plantCode)?.Title || li.plantTitle || '',
      partNumberLabel:
        li.partNumberLabel || Array.from(pnSets.get(plantCode) || []).join(', ') || '',
    };
  });
  const total = lines.reduce((acc, li) => acc + (Number(li.amount) || 0), 0);
  // Drop page-only helper fields (e.g. _supplierLabel) before export.
  const record = Object.fromEntries(
    Object.entries(invoice).filter(([k]) => !k.startsWith('_')),
  );
  exportInvoicePdf({
    invoice: { ...record, lineItems: lines, Total: total },
    periodLabel: formatPeriodLabel(invoice.PeriodStart, invoice.PeriodEnd),
    customer: supplier || null,
    lineItems: lines,
    total,
    isCreditMemo: String(invoice.InvoiceNumber || '').startsWith('CR-'),
  });
}

function formatPeriodLabel(startIso, endIso) {
  const fmt = (iso) => {
    const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  const s = fmt(startIso);
  const e = fmt(endIso);
  if (s && e) return `${s} – ${e}`;
  return s || e || '';
}
